import { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import Swal from "sweetalert2";
import { VariableGet, HijosHijosUpdate_ } from "../../../service/hijos";

const IVA = 0.16;

const OPCIONES_PAGO = [
  { value: "unico", label: "Unico" },
  { value: "semanal", label: "Semanal" },
  { value: "quincenal", label: "Quincenal" },
  { value: "mensual", label: "Mensual" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

const formularioDe = (concepto) => ({
  nombre: concepto?.Nombre ?? concepto?.nombre ?? "",
  proveedor_id: concepto?.proveedor_id ?? "",
  variables_id: concepto?.variables_id ?? "",
  cantidad_veces_pagos: concepto?.cantidad_veces_pagos ?? "",
  unidad: concepto?.unidad ?? "",
  x_cuantas_veces: concepto?.x_cuantas_veces ?? "",
  costo_unitario: concepto?.costo_unitario ?? "",
  con_iva: concepto?.con_iva === "1" || concepto?.con_iva === true || concepto?.con_iva === "true",
  fecha_inicial_pagos: concepto?.fecha_inical_pagos ?? "",
  opcion_pagos: concepto?.opcion_pagos ?? "",
  cuantos_pagos: concepto?.cuantos_pagos ?? "",
});

/**
 * Editar un concepto ya creado (proveedor, variable, montos y calendario de
 * pagos). El endpoint HijosHijosUpdate ya existia desde antes del rediseño de
 * la tabla, pero no habia ningun boton que lo llamara.
 */
export default function ModalEditarConcepto({
  abierto,
  toggle,
  concepto,
  token,
  proveedoresReales,
  onGuardado,
}) {
  const [form, setForm] = useState(() => formularioDe(concepto));
  const [variables, setVariables] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (abierto) {
      setForm(formularioDe(concepto));
      VariableGet(token).then((respuesta) => setVariables(respuesta ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto, concepto]);

  const actualizar = (cambios) => setForm((actual) => ({ ...actual, ...cambios }));

  const subtotal =
    (parseFloat(form.cantidad_veces_pagos) || 0) *
    (parseFloat(form.x_cuantas_veces) || 1) *
    (parseFloat(form.costo_unitario) || 0);
  const iva = form.con_iva ? subtotal * IVA : 0;
  const total = subtotal + iva;

  const guardar = async () => {
    if (!form.nombre.trim()) {
      Swal.fire({ icon: "error", title: "Falta el nombre del concepto" });
      return;
    }

    setGuardando(true);
    try {
      const data = new FormData();
      data.append("id", concepto.id);
      data.append("nombre", form.nombre.trim());
      data.append("proveedor_id", form.proveedor_id || "");
      data.append("variables_id", form.variables_id || "");
      data.append("con_iva", form.con_iva ? "1" : "0");
      data.append("cantidad_veces_pagos", form.cantidad_veces_pagos);
      data.append("unidad", form.unidad);
      data.append("x_cuantas_veces", form.x_cuantas_veces);
      data.append("costo_unitario", form.costo_unitario);
      data.append("fecha_inicial_pagos", form.fecha_inicial_pagos);
      data.append("opcion_pagos", form.opcion_pagos);
      data.append("cuantos_pagos", form.cuantos_pagos);
      data.append("subtotal", subtotal);
      data.append("iva", iva);
      data.append("total", total);

      const respuesta = await HijosHijosUpdate_(data, token);

      if (respuesta?.guardado === true) {
        Swal.fire({ icon: "success", title: "Concepto actualizado", confirmButtonColor: "#3b82f6" });
        onGuardado();
        toggle();
      } else {
        const primerError = respuesta?.errors ? Object.values(respuesta.errors)[0] : null;
        Swal.fire({
          icon: "error",
          title: "No se pudo guardar",
          text: primerError ? primerError[0] : "Revisa los datos e intenta de nuevo.",
        });
      }
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal size="lg" isOpen={abierto} toggle={toggle}>
      <ModalHeader toggle={toggle}>Editar concepto</ModalHeader>
      <ModalBody>
        <div className="row g-2">
          <div className="col-md-6">
            <label className="form-label">Concepto</label>
            <input
              className="form-control"
              value={form.nombre}
              onChange={(e) => actualizar({ nombre: e.target.value })}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Proveedor</label>
            <select
              className="form-control"
              value={form.proveedor_id}
              onChange={(e) => actualizar({ proveedor_id: e.target.value })}
            >
              <option value="">Sin proveedor</option>
              {proveedoresReales.map((p) => {
                const nombre = [p.nombre, p.apellido_p, p.apellido_m].filter(Boolean).join(" ");
                return (
                  <option key={p.id} value={p.id}>
                    {nombre}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="col-md-6">
            <label className="form-label">Nombre de la variable</label>
            <select
              className="form-control"
              value={form.variables_id}
              onChange={(e) => {
                const opt = variables.find((v) => String(v.value) === e.target.value);
                actualizar({
                  variables_id: e.target.value,
                  costo_unitario: opt ? opt.precio : form.costo_unitario,
                });
              }}
            >
              <option value="">Sin variable</option>
              {variables.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6 d-flex align-items-end">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="editar-concepto-iva"
                checked={form.con_iva}
                onChange={(e) => actualizar({ con_iva: e.target.checked })}
              />
              <label className="form-check-label" htmlFor="editar-concepto-iva">
                IVA
              </label>
            </div>
          </div>

          <div className="col-md-3">
            <label className="form-label">Cantidad</label>
            <input
              type="number"
              step="any"
              className="form-control"
              value={form.cantidad_veces_pagos}
              onChange={(e) => actualizar({ cantidad_veces_pagos: e.target.value })}
            />
          </div>

          <div className="col-md-3">
            <label className="form-label">Unidad</label>
            <input
              className="form-control"
              value={form.unidad}
              onChange={(e) => actualizar({ unidad: e.target.value })}
            />
          </div>

          <div className="col-md-2">
            <label className="form-label">X</label>
            <input
              type="number"
              step="any"
              className="form-control"
              value={form.x_cuantas_veces}
              onChange={(e) => actualizar({ x_cuantas_veces: e.target.value })}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Costo</label>
            <input
              type="number"
              step="any"
              className="form-control"
              value={form.costo_unitario}
              disabled={!!form.variables_id}
              onChange={(e) => actualizar({ costo_unitario: e.target.value })}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Fecha primer pago</label>
            <input
              type="date"
              className="form-control"
              value={form.fecha_inicial_pagos}
              onChange={(e) => actualizar({ fecha_inicial_pagos: e.target.value })}
            />
          </div>

          <div className="col-md-4">
            <label className="form-label">Opcion de pagos</label>
            <select
              className="form-control"
              value={form.opcion_pagos}
              onChange={(e) => actualizar({ opcion_pagos: e.target.value })}
            >
              <option value="">Sin opción</option>
              {OPCIONES_PAGO.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-4">
            <label className="form-label">Cuantos pagos</label>
            <input
              type="number"
              className="form-control"
              value={form.cuantos_pagos}
              onChange={(e) => actualizar({ cuantos_pagos: e.target.value })}
            />
          </div>

          <div className="col-md-12 text-end">
            <strong>
              Total: {total.toLocaleString("en-US", { style: "currency", currency: "USD" })}
            </strong>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="dark" onClick={guardar} disabled={guardando}>
          {guardando ? "Guardando..." : "Guardar"}
        </Button>
        <Button color="secondary" onClick={toggle} disabled={guardando}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}
